import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LANGS = ["fr", "en", "es", "pt"];
const slugify = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

// Catégories des articles (clé + libellé dans les 4 langues) — une catégorie encore utilisée ne peut pas être supprimée (refus serveur).
export const ResourceCategoriesDialog = ({ open, onOpenChange, categories, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setRows((categories || []).map((c) => ({ key: c.key, label: { ...c.label }, existing: true }))); }, [open, categories]);

  const setLabel = (i, lg, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, label: { ...row.label, [lg]: v }, key: row.existing ? row.key : (lg === "fr" ? slugify(v) : row.key) } : row)));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/resources/categories", { categories: rows.map(({ key, label }) => ({ key, label })) });
      toast.success("Catégories enregistrées"); onSaved?.(data.categories); onOpenChange(false);
    } catch (e) { toast.error(e?.friendlyMessage || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="res-cats-dialog">
        <DialogHeader>
          <DialogTitle>Catégories des ressources</DialogTitle>
          <DialogDescription>Libellés affichés aux lecteurs dans leur langue. La clé technique (adresse) est déduite du libellé français à la création.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="res-cats-list">
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-[11px] font-semibold uppercase" style={{ color: "var(--yalola-text-muted)" }}>
            {LANGS.map((lg) => <span key={lg}>{lg}</span>)}<span />
          </div>
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`res-cat-row-${i}`} data-key={row.key}>
              {LANGS.map((lg) => <Input key={lg} value={row.label[lg] || ""} onChange={(e) => setLabel(i, lg, e.target.value)} placeholder={lg === "fr" ? "Libellé FR *" : "—"} className="h-9 text-sm" data-testid={`res-cat-${i}-${lg}`} />)}
              <button type="button" onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-black/5" title="Supprimer" data-testid={`res-cat-del-${i}`}><Trash2 size={15} style={{ color: "#B91C1C" }} /></button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setRows((r) => [...r, { key: "", label: { fr: "", en: "", es: "", pt: "" }, existing: false }])} data-testid="res-cat-add"><Plus size={15} />Ajouter une catégorie</Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="res-cats-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="res-cats-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
